/**
 * Deal Investment Email Template
 * Professional, conversion-focused HTML template for investment property deals
 */

export interface DealEmailData {
  propertyAddress: string;
  shareLink: string;
  listPrice?: number;
  roi?: number;
  arv?: number;
  rehabCost?: number;
  profit?: number;
  beds?: number;
  baths?: number;
  sqft?: number;
  yearBuilt?: number;
  propertyImage?: string;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
}

export function generateDealEmailTemplate(data: DealEmailData): string {
  const {
    propertyAddress,
    shareLink,
    listPrice,
    roi,
    arv,
    rehabCost,
    profit,
    beds,
    baths,
    sqft,
    yearBuilt,
    propertyImage,
    agentName,
    agentPhone,
    agentEmail
  } = data;

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Investment Property Opportunity - ${propertyAddress}</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f4f7fa;">
  
  <!-- Wrapper Table -->
  <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f4f7fa;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        
        <!-- Main Container -->
        <table cellpadding="0" cellspacing="0" border="0" width="600" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
          
          <!-- Hero Section -->
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 30px; text-align: center;">
              <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 600; line-height: 1.3;">
                🏠 Exclusive Investment Opportunity
              </h1>
              <p style="margin: 10px 0 0; color: #ffffff; font-size: 16px; opacity: 0.9;">
                High-ROI Property Now Available
              </p>
            </td>
          </tr>
          
          <!-- Property Image (if available) -->
          ${propertyImage ? `
          <tr>
            <td style="padding: 0;">
              <img src="${propertyImage}" alt="${propertyAddress}" style="width: 100%; height: auto; display: block;">
            </td>
          </tr>
          ` : ''}
          
          <!-- Property Details -->
          <tr>
            <td style="padding: 30px;">
              <h2 style="margin: 0 0 10px; color: #2d3748; font-size: 24px; font-weight: 600;">
                ${propertyAddress}
              </h2>
              
              <!-- Property Info Bar -->
              <div style="background-color: #f7fafc; border-radius: 6px; padding: 15px; margin-bottom: 25px;">
                <table cellpadding="0" cellspacing="0" border="0" width="100%">
                  <tr>
                    ${beds ? `
                    <td align="center" style="font-size: 14px; color: #718096;">
                      <strong style="color: #2d3748; font-size: 16px;">${beds}</strong><br>
                      Beds
                    </td>
                    ` : ''}
                    ${baths ? `
                    <td align="center" style="font-size: 14px; color: #718096;">
                      <strong style="color: #2d3748; font-size: 16px;">${baths}</strong><br>
                      Baths
                    </td>
                    ` : ''}
                    ${sqft ? `
                    <td align="center" style="font-size: 14px; color: #718096;">
                      <strong style="color: #2d3748; font-size: 16px;">${Number(sqft).toLocaleString()}</strong><br>
                      Sq Ft
                    </td>
                    ` : ''}
                    ${yearBuilt ? `
                    <td align="center" style="font-size: 14px; color: #718096;">
                      <strong style="color: #2d3748; font-size: 16px;">${yearBuilt}</strong><br>
                      Year Built
                    </td>
                    ` : ''}
                  </tr>
                </table>
              </div>
              
              <!-- Key Investment Metrics -->
              <h3 style="margin: 25px 0 15px; color: #2d3748; font-size: 20px; font-weight: 600;">
                💰 Investment Highlights
              </h3>
              
              <!-- Metrics Grid -->
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 25px;">
                <tr>
                  <td width="48%" valign="top">
                    ${listPrice ? `
                    <div style="background: #edf2f7; border-radius: 6px; padding: 20px; margin-bottom: 10px; text-align: center;">
                      <div style="color: #718096; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">List Price</div>
                      <div style="color: #2d3748; font-size: 24px; font-weight: 700;">$${Number(listPrice).toLocaleString()}</div>
                    </div>
                    ` : ''}
                    
                    ${rehabCost ? `
                    <div style="background: #edf2f7; border-radius: 6px; padding: 20px; text-align: center;">
                      <div style="color: #718096; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Est. Rehab Cost</div>
                      <div style="color: #2d3748; font-size: 24px; font-weight: 700;">$${Number(rehabCost).toLocaleString()}</div>
                    </div>
                    ` : ''}
                  </td>
                  <td width="4%"></td>
                  <td width="48%" valign="top">
                    ${roi ? `
                    <div style="background: #f0fff4; border: 2px solid #68d391; border-radius: 6px; padding: 18px; margin-bottom: 10px; text-align: center;">
                      <div style="color: #22543d; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Estimated ROI</div>
                      <div style="color: #22543d; font-size: 28px; font-weight: 700;">${Number(roi).toFixed(1)}%</div>
                    </div>
                    ` : ''}
                    
                    ${profit ? `
                    <div style="background: #f0fff4; border-radius: 6px; padding: 20px; text-align: center;">
                      <div style="color: #22543d; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Potential Profit</div>
                      <div style="color: #22543d; font-size: 24px; font-weight: 700;">$${Number(profit).toLocaleString()}</div>
                    </div>
                    ` : ''}
                  </td>
                </tr>
              </table>
              
              <!-- ARV if available -->
              ${arv ? `
              <div style="background: #fef5e7; border-left: 4px solid #f39c12; padding: 15px; margin-bottom: 25px;">
                <strong style="color: #d68910;">After Repair Value (ARV):</strong> 
                <span style="font-size: 20px; color: #d68910; font-weight: 700;">$${Number(arv).toLocaleString()}</span>
              </div>
              ` : ''}
              
              <!-- What's Included -->
              <h3 style="margin: 25px 0 15px; color: #2d3748; font-size: 20px; font-weight: 600;">
                📊 Your Comprehensive Report Includes:
              </h3>
              
              <ul style="margin: 0 0 25px; padding-left: 20px; color: #4a5568; line-height: 1.8;">
                <li>Detailed property analysis & photos</li>
                <li>Comparable sales data (comps)</li>
                <li>Itemized rehabilitation cost breakdown</li>
                <li>Cash flow projections</li>
                <li>Market trends & neighborhood analysis</li>
                <li>Investment timeline & exit strategies</li>
              </ul>
              
              <!-- CTA Section -->
              <div style="background: #f7fafc; border-radius: 6px; padding: 25px; text-align: center; margin-bottom: 25px;">
                <p style="margin: 0 0 20px; color: #4a5568; font-size: 16px;">
                  <strong>Ready to explore this opportunity?</strong><br>
                  Access your personalized investment analysis now.
                </p>
                
                <a href="${shareLink}" style="display: inline-block; background: #667eea; color: #ffffff; text-decoration: none; padding: 16px 40px; border-radius: 6px; font-size: 16px; font-weight: 600; box-shadow: 0 4px 6px rgba(102, 126, 234, 0.25);">
                  View Full Investment Report →
                </a>
                
                <p style="margin: 15px 0 0; color: #a0aec0; font-size: 12px;">
                  This link expires in 30 days • No login required
                </p>
              </div>
              
              <!-- Agent Contact -->
              ${agentName ? `
              <div style="border-top: 1px solid #e2e8f0; padding-top: 25px; text-align: center;">
                <p style="margin: 0 0 5px; color: #718096; font-size: 14px;">Questions? I'm here to help!</p>
                <p style="margin: 0; color: #2d3748; font-size: 16px; font-weight: 600;">${agentName}</p>
                ${agentPhone ? `<p style="margin: 5px 0; color: #4a5568; font-size: 14px;">📱 ${agentPhone}</p>` : ''}
                ${agentEmail ? `<p style="margin: 5px 0; color: #4a5568; font-size: 14px;">✉️ ${agentEmail}</p>` : ''}
              </div>
              ` : ''}
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="background-color: #2d3748; padding: 30px; text-align: center;">
              <p style="margin: 0 0 10px; color: #ffffff; font-size: 14px;">
                Powered by KairosIQ - AI-Driven Real Estate Intelligence
              </p>
              <p style="margin: 0; color: #a0aec0; font-size: 12px;">
                This email contains confidential information intended for the recipient only.
              </p>
            </td>
          </tr>
          
        </table>
        
      </td>
    </tr>
  </table>
  
</body>
</html>
  `.trim();
}

/**
 * Generate a simple text version of the email for fallback
 */
export function generateDealEmailText(data: DealEmailData): string {
  const {
    propertyAddress,
    shareLink,
    listPrice,
    roi,
    profit,
    beds,
    baths,
    sqft
  } = data;

  return `
Investment Property Opportunity: ${propertyAddress}

Property Details:
${beds ? `• ${beds} Bedrooms` : ''}
${baths ? `• ${baths} Bathrooms` : ''}
${sqft ? `• ${Number(sqft).toLocaleString()} Square Feet` : ''}

Investment Highlights:
${listPrice ? `• List Price: $${Number(listPrice).toLocaleString()}` : ''}
${roi ? `• Estimated ROI: ${Number(roi).toFixed(1)}%` : ''}
${profit ? `• Potential Profit: $${Number(profit).toLocaleString()}` : ''}

View the complete investment analysis report:
${shareLink}

This report includes comparable sales data, rehabilitation costs, and detailed ROI projections.

Best regards,
Your Real Estate Professional
  `.trim();
}